import {
	type LocalizedString,
	type ProductVariant,
} from '@commercetools/platform-sdk'
import { Link, useLoaderData, useLocation } from '@remix-run/react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { useTranslation } from 'react-i18next'
import ImageComponent from '#app/components/common/image-component'
import PlentyBody from '#app/components/typography/plenty-body'
import {
	Carousel,
	CarouselContent,
	CarouselItem,
	CarouselProgress,
} from '#app/components/ui/carousel.tsx'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader } from '#app/routes/_public+/product+/$styleNumber'
import { cn } from '#app/utils/misc'
import { type StyleVariant } from '#types/product'
import { PriceTag } from './complement-product-price-tag'
interface ComplementProductsProps {
	activeStyle: StyleVariant
}

interface VariantWithProductInfo extends ProductVariant {
	productInfo: {
		name: LocalizedString
		styleNumber: string
	}
}

const ComplementProducts = ({ activeStyle }: ComplementProductsProps) => {
	const data = useLoaderData<typeof loader>()
	const { t } = useTranslation('product_details_page')
	if (!data.complementProductsArray) {
		return null
	}
	const complementProductsArray = data.complementProductsArray

	const activeStyleKey = `${activeStyle.color}-${activeStyle.variantId}`
	const complementForActiveStyle = complementProductsArray.find(
		(it: any) => it.key === activeStyleKey,
	)
	if (complementProductsArray.length === 0 || !complementForActiveStyle) {
		return null
	}

	return (
		<Carousel
			className="relative w-full"
			plugins={[WheelGesturesPlugin()]}
			opts={{ dragFree: true }}
		>
			<div className="flex flex-col gap-y-4">
				<div className="flex flex-row items-center justify-between">
					<PlentyBody className="font-semibold" size="lg">
						{t('complement_product.title')}
					</PlentyBody>
					<CarouselProgress />
				</div>
				<div className="flex flex-col overflow-x-auto scrollbar-hide">
					<CarouselContent className="gap-4 pl-4">
						{complementForActiveStyle.variants.map((styleOption: any) => {
							return (
								<CarouselItem
									key={`${styleOption.key}-${styleOption.variant.sku}`}
									className="relative w-full max-w-[184px] shrink-0 pl-0"
								>
									<VariantCard
										variant={styleOption.variant}
										key={`${styleOption.key}-${styleOption.variant.sku}`}
										styleOptionKey={styleOption.key}
									/>
								</CarouselItem>
							)
						})}
					</CarouselContent>
				</div>
			</div>
		</Carousel>
	)
}

const VariantCard = ({
	variant,
	styleOptionKey,
}: {
	variant: VariantWithProductInfo
	styleOptionKey: string
}) => {
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const location = useLocation()
	return (
		<Link
			className="flex flex-col gap-4"
			key={variant.key}
			to={`/product/${variant.productInfo.styleNumber}?style=${styleOptionKey}`}
			onClick={() => {
				trackAmplitudeEvent('complement product click', {
					product: `${variant.productInfo.styleNumber}?style=${styleOptionKey}`,
					referer: location.pathname + location.search,
				})
			}}
		>
			{variant.images && variant.images[0] && (
				<div className="relative flex">
					<div className="special-wrap">
						<ImageComponent
							size="original"
							src={variant.images[0].url}
							alt={variant.sku}
							className={cn(
								`aspect-product-image h-full  min-h-[265px] max-w-[168px] rounded-lg object-cover transition duration-300 ease-in-out md:min-h-[220px] `,
							)}
							loading={'lazy'}
						/>
					</div>
				</div>
			)}

			<div className="flex flex-col">
				<PlentyBody size="md" className="text-beige-100">
					{(variant.attributes &&
						variant.attributes
							.find((attr: { name: string }) => attr.name === 'brand')
							?.value.toUpperCase()) ??
						'Unknown Brand'}
				</PlentyBody>
				<div className="flex flex-col">
					<PlentyBody size="md">{variant.productInfo.name.en}</PlentyBody>
				</div>
				<div>
					<PriceTag variant={variant} />
				</div>
			</div>
		</Link>
	)
}

export default ComplementProducts
